/* eslint-disable max-lines */
import styled from 'styled-components';
import { HeColours } from '../../../styles/HeColours';
import { ThemeColours } from '../../../styles/ThemeColours';
import {
  Breakpoint,
  FontWeight,
  FONT_STACK,
  RESET_IOS_INPUT_STYLE,
  STANDARD_TRANSITION,
  FontSize,
  SHADOW,
} from '../../../styles/constants';
import { propSwitch } from '../../../util/propSwitch';
import { IconCross } from '../../icons/IconCross';
import { IconSearch } from '../../icons/IconSearch';
import { IconSpecialty } from '../../icons/IconSpecialty';
import { SearchLocationMarker } from '../../icons/SearchLocationMarker';

export const SearchBarView = styled.div<{
  isSearchMultiLine?: boolean;
  isSearchInstructionVisible?: boolean;
}>`
  ${({ isSearchInstructionVisible }) =>
    isSearchInstructionVisible &&
    `
    margin-top: 40px;
  `}
  align-items: center;
  background-color: ${HeColours.gray[50]};
  border: 1px solid ${ThemeColours.mono200};
  border-radius: 24px;
  box-shadow: ${SHADOW.LARGE};
  box-sizing: border-box;
  display: flex;
  flex-direction: ${propSwitch('isSearchMultiLine', 'column', 'row')};
  flex-grow: 1;
  max-height: ${propSwitch('isSearchMultiLine', '94px', '48px')};
  max-width: ${propSwitch('isSearchMultiLine', '100%', '78%')};
  ${Breakpoint.SMALL} {
    max-width: 760px;
  }
`;

export const Bar = styled.div<{ isSearchMultiLine?: boolean }>`
  border: none;
  box-sizing: border-box;
  height: 46px;
  max-width: ${propSwitch('isSearchMultiLine', '100%', '58%')};
  min-width: ${propSwitch('isSearchMultiLine', '', '30%')};
  position: relative;
  width: ${propSwitch('isSearchMultiLine', '100%', 'auto')};
  &:hover {
    background: none;
    border-radius: 0px;
  }

  ${Breakpoint.SMALL} {
    &:hover {
      background: ${ThemeColours.mono100};
      border-radius: 23px;
    }

    max-width: 400px;
    min-width: 0;
    overflow: visible;
    width: ${propSwitch('isSearchMultiLine', '100%', '60%')};
  }
`;

export const BarLocation = styled(Bar)<{
  isSearchMultiLine: boolean;
  isBorderTopVisible?: boolean;
}>`
  &:before {
    border-left: ${propSwitch('isSearchMultiLine', 'none', '1px solid')};
    border-left-color: ${ThemeColours.mono200};
    content: '';
    height: 50%;
    left: 0;
    position: absolute;
    top: 12px;
    width: 1px;
  }
  border-top: ${propSwitch('isBorderTopVisible', '1px solid', 'none')};
  border-top-color: ${ThemeColours.mono200};
  flex-grow: 1;
  max-width: none;
  width: ${propSwitch('isSearchMultiLine', '100%', 'none')};

  ${Breakpoint.SMALL} {
    max-width: 360px;
    width: 100%;
  }
`;

export const InputSpecialty = styled.input`
  ${STANDARD_TRANSITION};
  ${RESET_IOS_INPUT_STYLE};
  ${FONT_STACK}
  background: none;
  border: none;
  box-sizing: border-box;
  color: ${ThemeColours.black};
  font-size: ${FontSize.REGULAR};
  font-weight: ${FontWeight.REGULAR};
  height: 100%;
  max-width: 400px;
  outline: 0;
  overflow: hidden;
  padding-left: 48px;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  ::placeholder,
  ::-webkit-input-placeholder {
    color: ${ThemeColours.mono600};
    font-weight: ${FontWeight.LIGHT};
  }
  &:focus {
    background: ${ThemeColours.backgroundPrimary};
    border-radius: 24px;
  }
  ${Breakpoint.SMALL} {
    padding-left: 48px;
  }
`;

export const SearchIconBackground = styled.div`
  align-items: center;
  background-color: ${ThemeColours.accent400};
  border-radius: 32px;
  cursor: pointer;
  display: flex;
  fill: currentColor;
  height: 32px;
  position: absolute;
  right: 8px;
  top: 8px;
  width: 32px;
`;

export const SearchIcon = styled(IconSearch)`
  color: ${ThemeColours.white};
  height: 16px;
  margin: 0 auto;
  width: 16px;
`;

export const LocationMarkerIcon = styled(SearchLocationMarker)<{
  isSearchMultiLine?: boolean;
}>`
  cursor: pointer;
  height: ${propSwitch('isSearchMultiLine', '24', '16')}px;
  left: ${propSwitch('isSearchMultiLine', '16', '8')}px;
  position: absolute;
  top: ${propSwitch('isSearchMultiLine', '12', '16')}px;
  width: ${propSwitch('isSearchMultiLine', '24', '16')}px;
  ${Breakpoint.SMALL} {
    height: 24px;
    left: 16px;
    right: 56px;
    top: 12px;
    width: 24px;
  }
`;

export const SpecialtyIcon = styled(IconSpecialty)<{
  isSearchMultiLine?: boolean;
}>`
  cursor: pointer;
  height: ${propSwitch('isSearchMultiLine', '24', '16')}px;
  left: 16px;
  position: absolute;
  top: ${propSwitch('isSearchMultiLine', '12', '16')}px;
  width: ${propSwitch('isSearchMultiLine', '24', '16')}px;
  ${Breakpoint.SMALL} {
    height: 24px;
    left: 16px;
    right: 56px;
    top: 12px;
    width: 24px;
  }
`;

export const CompactSearchIconBackground = styled.div`
  align-items: center;
  border: 1px solid ${ThemeColours.mono100};
  border-radius: 42px;
  cursor: pointer;
  display: flex;
  fill: currentColor;
  height: 42px;
  margin-right: 12px;
  width: 42px;
`;

export const CompactSearchIcon = styled(IconSearch)`
  color: ${ThemeColours.black};
  height: 24px;
  margin: 0 auto;
  width: 24px;
`;

export const CrossIcon = styled(IconCross)`
  color: ${ThemeColours.black};
  cursor: pointer;
  height: 24px;
  position: absolute;
  right: 12px;
  top: 12px;
  width: 24px;
  ${Breakpoint.SMALL} {
    right: 56px;
  }
`;

export const SpecialtyPlaceholder = styled.div<{
  isSearchMultiLine?: boolean;
}>`
  ${RESET_IOS_INPUT_STYLE};
  ${FONT_STACK}
  align-content: center;
  border-radius: 24px;
  box-sizing: border-box;
  color: ${ThemeColours.mono900};
  font-size: ${propSwitch('isSearchMultiLine', '16', '14')}px;
  font-weight: ${propSwitch('isSearchMultiLine', '400', '600')};
  height: 100%;
  justify-content: center;
  margin-top: 2px;
  overflow: hidden;
  padding-left: ${propSwitch('isSearchMultiLine', '48', '35')}px;
  position: relative;
  text-overflow: ellipsis;
  top: 13px;
  white-space: nowrap;
`;

export const LocationPlaceholder = styled.div<{
  isSearchMultiLine?: boolean;
  $noLocation?: boolean;
}>`
  ${RESET_IOS_INPUT_STYLE};
  ${FONT_STACK}
  align-content: center;
  border-radius: 24px;
  box-sizing: border-box;
  color: ${propSwitch('$noLocation', ThemeColours.mono600, ThemeColours.mono900)};
  font-size: ${propSwitch('isSearchMultiLine', '16', '14')}px;
  font-weight: ${propSwitch('isSearchMultiLine', '400', '600')};
  height: 100%;
  justify-content: center;
  margin-top: 2px;
  overflow: hidden;
  padding-left: ${propSwitch('isSearchMultiLine', '48', '27')}px;
  position: relative;
  text-overflow: ellipsis;
  top: 13px;
  white-space: nowrap;
  ${Breakpoint.SMALL} {
    padding-left: 48px;
  }
`;

export const ClearIcon = styled(IconCross)<{
  isHidden?: boolean;
  isSpecialty?: boolean;
}>`
  background: ${ThemeColours.mono100};
  border-radius: 50%;
  display: ${propSwitch('isHidden', 'none', 'block')};
  height: 20px;
  position: absolute;
  right: ${propSwitch('isSpecialty', '10', '8')}px;
  top: 13px;
  width: 20px;
  ${Breakpoint.SMALL} {
    right: ${propSwitch('isSpecialty', '10', '52')}px;
  }
`;
